import config from "@/config";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { clearLocalStorage, getSession, setSession } from "../utils/auth";
import { readStringFlag } from "../utils/localFlag";
import { refresh } from "./security";

let prepareRequest = axios.create({
    baseURL: config.baseApi,
    timeout: 30000,
});

prepareRequest.defaults.headers["Content-Type"] = "application/json";

export const requestWithoutToken = prepareRequest;

/**
 * Uploads a file to the backend using the provided endpoint.
 * @param {string} url - The API endpoint for file upload.
 * @param {File} file - The file to be uploaded.
 * @param {string} fieldName - The name of the form field to send the file under. Default is "file".
 * @returns {Promise} - Resolves with the API response or rejects with an error.
 */
export async function uploadFile(url, file, fieldName = "file") {
    const formData = new FormData();
    formData.append(fieldName, file);

    try {
        const response = await requestWithToken.post(url, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response;
    } catch (error) {
        console.error("File upload failed:", error);
        throw error;
    }
}

prepareRequest.interceptors.request.use(
    (config) => {
        let session = getSession();
        if (session) {
            // get token
            let token = "";
            let diff = 0;
            let error = false;
            try {
                // get time diff
                const timeDiff = Number(
                    localStorage.getItem("time_difference")
                );
                // get local time
                const localDate = new Date();
                const localTime = localDate.getTime();
                // get expiration time
                let expDate = new Date(0);
                token = jwt_decode(session.token);
                expDate.setUTCSeconds(token.exp);
                let expTime = expDate.getTime();
                if (timeDiff > 0) {
                    expTime = expTime - timeDiff;
                } else {
                    expTime = expTime + Math.abs(timeDiff);
                }
                // set countdown time
                diff = expTime - localTime;
            } catch (err) {
                error = true;
            } finally {
                if (diff <= 0 || error) {
                    clearLocalStorage();
                    window.location.href = "/";
                }
                config.headers["Authorization"] = `Bearer ${session.token}`;
            }
            // Scope the request to the user's selected company. Read straight
            // from localStorage (not the store) to avoid an api<->store import
            // cycle; switching company persists here + reloads, so the value
            // is always consistent at request time. The backend validates it
            // against the user's companies (get_company_user).
            const activeCompanyId = readStringFlag("activeCompanyId");
            if (activeCompanyId) {
                config.headers["X-Active-Company"] = activeCompanyId;
            }
        }
        return config;
    },
    (error) => {
        Promise.reject(error);
    }
);

// declare a response interceptor
prepareRequest.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        return new Promise((resolve, reject) => {
            Promise.reject(error).catch((error) => {
                const status = error?.response?.status || undefined;
                if (
                    403 === status &&
                    !error.response.config.skipAuthRedirect &&
                    error.response.config.url !== "/logout/"
                ) {
                    const session = getSession();
                    let redirectTo = "/";
                    refresh(session.token)
                        .then((res) => {
                            setSession(res.data);
                            redirectTo = "/go-to-plan";
                        })
                        .catch(() => {
                            clearLocalStorage();
                        })
                        .finally(() => {
                            window.location.href = redirectTo;
                            return;
                        });
                } else if (
                    403 === status &&
                    error.response.config.url === "/logout/"
                ) {
                    clearLocalStorage();
                    window.location.href = "/";
                    return;
                } else if (401 === status) {
                    clearLocalStorage();
                    window.location.href = "/";
                    return;
                }
                return reject(error);
            });
        });
    }
);

export const requestWithToken = prepareRequest;
